"use client"

import { useState, useEffect, useCallback } from "react"

const SpaceEventSystem = ({ gameState, setGameState, addNotification }) => {
  const [activeEvents, setActiveEvents] = useState([])
  const [eventHistory, setEventHistory] = useState([])
  const [lastEventCheck, setLastEventCheck] = useState(Date.now())

  const NASA_API_KEY = "kgndK5blpMHA4CsrmXFhIEctpLmpVgSt0lWWl5sW"

  const fetchSpaceData = useCallback(async () => {
    try {
      // Use APOD (Astronomy Picture of the Day) - no CORS issues
      const apodResponse = await fetch(`https://api.nasa.gov/planetary/apod?api_key=${NASA_API_KEY}&count=1`)

      if (apodResponse.ok) {
        const apodData = await apodResponse.json()
        console.log("NASA APOD data received:", apodData)

        // Generate space events based on APOD data
        if (Math.random() < 0.3) {
          triggerSpaceEvent("cosmic")
        }
      }
    } catch (error) {
      console.log("NASA API unavailable, using simulated events")
    }

    // Always trigger random events for gameplay
    if (Math.random() < 0.4) {
      triggerRandomSpaceEvent()
    }
  }, [])

  const triggerSpaceEvent = (type) => {
    const events = {
      cosmic: [
        {
          title: "🌌 Cosmic Radiation Spike",
          description: "High-energy particles detected! Unshielded colonists at risk!",
          type: "danger",
          effects: { population: -15, colonyHealth: -20 },
        },
      ],
    }

    const event = events[type][0]
    executeEvent(event)
  }

  const triggerRandomSpaceEvent = () => {
    const events = [
      {
        title: "☀️ Solar Flare Incoming",
        description: "Massive solar flare detected! Electronics and unprotected colonists in danger!",
        type: "danger",
        effects: { energy: -100, population: -25, colonyHealth: -15 },
        canPrevent: "Shield Generator",
      },
      {
        title: "☄️ Asteroid Impact",
        description: "Small asteroid struck the colony! Infrastructure damaged!",
        type: "danger",
        effects: { materials: -75, buildingDamage: 20 },
      },
      {
        title: "🌪️ Dust Storm",
        description: "Massive dust storm reducing solar efficiency and visibility!",
        type: "warning",
        effects: { energy: -50, food: -25 },
      },
      {
        title: "❄️ Temperature Drop",
        description: "Unexpected temperature drop! Energy consumption increased!",
        type: "warning",
        effects: { energy: -60, colonyHealth: -10 },
      },
      {
        title: "💧 Water Leak Detected",
        description: "Critical water system failure! Immediate repairs needed!",
        type: "danger",
        effects: { water: -100, colonyMorale: -15 },
      },
      {
        title: "🔬 Scientific Discovery",
        description: "Research team made a breakthrough! Knowledge gained!",
        type: "success",
        effects: { knowledge: 50, colonyMorale: 10 },
      },
    ]

    const event = events[Math.floor(Math.random() * events.length)]
    executeEvent(event)
  }

  const executeEvent = (event) => {
    // Check if event can be prevented
    if (event.canPrevent && gameState.buildings.some((b) => b.name.includes(event.canPrevent) && b.health > 50)) {
      addNotification({
        type: "success",
        title: "🛡️ Threat Neutralized",
        description: `${event.canPrevent} successfully protected the colony from ${event.title}!`,
      })
      return
    }

    // Apply event effects
    setGameState((prev) => {
      const newState = { ...prev }

      // Apply resource effects
      Object.entries(event.effects).forEach(([key, value]) => {
        if (key === "population") {
          newState.resources.population = Math.max(0, newState.resources.population + value)
          if (value < 0) {
            addNotification({
              type: "error",
              title: "💀 Casualties Reported",
              description: `${Math.abs(value)} colonists lost due to ${event.title}!`,
            })
          }
        } else if (key === "colonyHealth") {
          newState.colonyHealth = Math.max(0, Math.min(100, newState.colonyHealth + value))
        } else if (key === "colonyMorale") {
          newState.colonyMorale = Math.max(0, Math.min(100, newState.colonyMorale + value))
        } else if (key === "buildingDamage") {
          // Damage random buildings
          newState.buildings = newState.buildings.map((building) => ({
            ...building,
            health: Math.max(0, building.health - value),
          }))
        } else if (newState.resources[key] !== undefined) {
          newState.resources[key] = Math.max(0, newState.resources[key] + value)
        }
      })

      return newState
    })

    // Show event notification
    addNotification({
      type: event.type,
      title: event.title,
      description: event.description,
    })

    // Add to event history
    setEventHistory((prev) => [
      {
        ...event,
        timestamp: Date.now(),
        id: Date.now(),
      },
      ...prev.slice(0, 9),
    ])
  }

  const checkForSpaceEvents = useCallback(async () => {
    try {
      // Fetch real NASA data with your API key
      const [solarResponse, asteroidResponse, issResponse] = await Promise.allSettled([
        fetch(`https://api.nasa.gov/DONKI/FLR?api_key=${NASA_API_KEY}&startDate=2024-01-01&endDate=2024-12-31`),
        fetch(`https://api.nasa.gov/neo/rest/v1/feed?api_key=${NASA_API_KEY}`),
        fetch(
          `https://api.nasa.gov/planetary/earth/assets?lon=-95.33&lat=29.78&date=2024-01-01&api_key=${NASA_API_KEY}`,
        ),
      ])

      // Process Solar Flares
      if (solarResponse.status === "fulfilled") {
        const solarData = await solarResponse.value.json()
        const recentFlares = solarData.filter((flare) => {
          const flareDate = new Date(flare.beginTime)
          const daysSince = (Date.now() - flareDate.getTime()) / (1000 * 60 * 60 * 24)
          return daysSince <= 7 // Within last week
        })

        if (recentFlares.length > 0) {
          const strongFlares = recentFlares.filter(
            (flare) => flare.classType && (flare.classType.startsWith("M") || flare.classType.startsWith("X")),
          )

          if (strongFlares.length > 0) {
            triggerSolarFlareEvent(strongFlares[0])
          }
        }
      }

      // Process Asteroids
      if (asteroidResponse.status === "fulfilled") {
        const asteroidData = await asteroidResponse.value.json()
        const hazardousAsteroids = Object.values(asteroidData.near_earth_objects || {})
          .flat()
          .filter((asteroid) => asteroid.is_potentially_hazardous_asteroid)

        if (hazardousAsteroids.length > 0) {
          triggerAsteroidEvent(hazardousAsteroids[0])
        }
      }

      // Process ISS/Weather data
      if (issResponse.status === "fulfilled") {
        triggerWeatherEvent()
      }
    } catch (error) {
      console.log("Using fallback space events")
      // Fallback random events
      if (Math.random() < 0.1) {
        triggerRandomEvent()
      }
    }
  }, [])

  const triggerSolarFlareEvent = (flareData) => {
    const intensity = flareData.classType?.startsWith("X") ? "extreme" : "moderate"

    addNotification({
      type: "warning",
      title: "☀️ Solar Flare Detected",
      description: `${flareData.classType} class solar flare is affecting your colony's electronics!`,
    })

    // Damage solar arrays and electronics
    setGameState((prev) => ({
      ...prev,
      resources: {
        ...prev.resources,
        energy: Math.max(0, prev.resources.energy - (intensity === "extreme" ? 100 : 50)),
      },
      buildings: prev.buildings.map((building) =>
        building.name.includes("Solar") || building.name.includes("Communication")
          ? { ...building, health: Math.max(0, building.health - (intensity === "extreme" ? 20 : 10)) }
          : building,
      ),
    }))
  }

  const triggerAsteroidEvent = (asteroidData) => {
    addNotification({
      type: "error",
      title: "☄️ Asteroid Threat",
      description: `Asteroid ${asteroidData.name} detected! Potential impact risk to infrastructure.`,
    })

    // Random building damage
    setGameState((prev) => ({
      ...prev,
      buildings: prev.buildings.map((building) =>
        Math.random() < 0.3 // 30% chance each building gets damaged
          ? { ...building, health: Math.max(0, building.health - Math.floor(Math.random() * 15 + 5)) }
          : building,
      ),
    }))
  }

  const triggerWeatherEvent = () => {
    addNotification({
      type: "info",
      title: "🌍 Earth Weather Update",
      description: "Receiving weather data from Earth monitoring stations. Colony systems updated.",
    })

    // Small knowledge bonus
    setGameState((prev) => ({
      ...prev,
      resources: { ...prev.resources, knowledge: prev.resources.knowledge + 10 },
    }))
  }

  const triggerRandomEvent = () => {
    const events = [
      {
        type: "info",
        title: "🔬 Scientific Discovery",
        description: "Your research team has made a breakthrough in atmospheric processing!",
        effect: () =>
          setGameState((prev) => ({
            ...prev,
            resources: { ...prev.resources, knowledge: prev.resources.knowledge + 25 },
          })),
      },
      {
        type: "warning",
        title: "🌪️ Dust Storm",
        description: "A massive dust storm is reducing solar panel efficiency.",
        effect: () =>
          setGameState((prev) => ({
            ...prev,
            resources: { ...prev.resources, energy: Math.max(0, prev.resources.energy - 30) },
          })),
      },
      {
        type: "success",
        title: "💧 Water Discovery",
        description: "Underground water reserves discovered near the colony!",
        effect: () =>
          setGameState((prev) => ({
            ...prev,
            resources: { ...prev.resources, water: prev.resources.water + 100 },
          })),
      },
    ]

    const event = events[Math.floor(Math.random() * events.length)]
    addNotification(event)
    if (event.effect) event.effect()
  }

  useEffect(() => {
    const interval = setInterval(fetchSpaceData, 30000) // Every 30 seconds
    fetchSpaceData() // Initial check
    return () => clearInterval(interval)
  }, [fetchSpaceData])

  useEffect(() => {
    const interval = setInterval(checkForSpaceEvents, 60000) // Check every minute
    checkForSpaceEvents() // Initial check
    return () => clearInterval(interval)
  }, [checkForSpaceEvents])

  return null // This component doesn't render anything visible
}

export default SpaceEventSystem
